import React, { useState } from 'react';
import { evaluateResistanceAlerts } from '../utils/microbiologyTemplateEngine';

export default function MicrobiologyAIModal({ data, isOpen, onClose }) {
  const [selectedPrompt, setSelectedPrompt] = useState('summary');

  if (!isOpen || !data) return null;

  const currentIsolates = data.isolates || [];
  const allResistanceAlerts = currentIsolates.flatMap((i) => evaluateResistanceAlerts(i));

  // Pre-configured Contextual Clinical Queries
  const promptOptions = [
    {
      id: 'summary',
      title: '📊 Comprehensive Clinical Case Summary',
      generate: () => `
**Clinical Microbiology Case Briefing**
• **Patient**: ${data.patientName} (${data.ageDisplay}, ${data.gender}) | UHID: \`${data.patientID}\`
• **Specimen**: ${data.specimenName} (Inoculated: ${data.daysInIncubation || 0} days ago)
• **Microscopy**: ${data.gramStain || data.pusCells || 'Pending smear observation'}
• **Culture Finding**: ${data.cultureStatus} (${data.colonyCount || 'No colonies recorded'})
• **Identified Isolates**: ${
        currentIsolates.map((i) => `${i.organismName} [${i.gramType}]`).join(', ') || 'None identified'
      }
• **Antimicrobial Resistance Flags**: ${
        allResistanceAlerts.length > 0
          ? allResistanceAlerts.map((a) => a.name).join(', ')
          : 'No high-risk epidemiological resistance markers flagged.'
      }
• **Infection Control Status**: ${data.isCriticalAlert ? '🚨 CRITICAL ALERT ACTIVE' : 'Standard Routine Precautions'}
• **Regulatory Breakpoint Standard**: EUCAST Breakpoints v16.1 (2026 Edition)
      `
    },
    {
      id: 'ast_explain',
      title: '💊 AST Anomalies & Therapeutic Guidance',
      generate: () => {
        const resistantDrugs = currentIsolates
          .flatMap((i) => i.astResults || [])
          .filter((r) => r.finalInterpretation === 'R');

        const susceptibleDrugs = currentIsolates
          .flatMap((i) => i.astResults || [])
          .filter((r) => r.finalInterpretation === 'S');

        return `
**Antimicrobial Stewardship & AST Susceptibility Analysis**
• **Primary Pathogen(s)**: ${currentIsolates.map((i) => i.organismName).join(', ') || 'Awaiting organism'}
• **Active Breakpoints Applied**: EUCAST standard clinical zone diameter & MIC thresholds.
• **Resistant Antimicrobials (${resistantDrugs.length})**: ${
          resistantDrugs.map((d) => `${d.drugName} (${d.drugCode || 'Rx'})`).join(', ') || 'None identified'
        }
• **Susceptible Antimicrobials (${susceptibleDrugs.length})**: ${
          susceptibleDrugs.map((d) => d.drugName).join(', ') || 'None measured yet'
        }
• **Clinical Expert Guidance**:
  1. For Gram-negative Enterobacterales, avoid third-generation cephalosporins if ESBL screen is flagged.
  2. Confirm Nitrofurantoin and Fosfomycin applicability for lower urinary tract infections only.
  3. Ensure all beta-lactam interpretations align with intrinsic resistance standards.
        `;
      }
    },
    {
      id: 'comments_suggest',
      title: '📝 Recommend Contextual Report Remarks',
      generate: () => `
**Suggested Diagnostic Comments for Report:**
1. *"Growth of ${currentIsolates[0]?.organismName || 'significant pathogen'} (>10^5 CFU/mL) in pure monomorphic culture. Susceptibility results determined via EUCAST standard disk diffusion."*
2. *"Please correlate with patient clinical presentation and urinalysis parameters before initiating antimicrobial therapy."*
3. *${
        allResistanceAlerts.length > 0
          ? '🚨 "Infection Control Alert: Phenotypic resistance pattern detected. Contact precautions recommended."'
          : '"Routine surveillance recommended."'
      }*
      `
    },
    {
      id: 'infection_control',
      title: '🚨 Infection Control & Surveillance Check',
      generate: () => `
**Hospital Infection Control & Outbreak Surveillance Audit**
• **Surveillance Status**: ${allResistanceAlerts.length > 0 ? '⚠️ ACTION REQUIRED' : '✓ Normal'}
• **Detected Mechanisms**: ${
        allResistanceAlerts.map((a) => `${a.code}: ${a.name} (${a.recommendedAction})`).join('\n• ') ||
        'No multi-drug resistant organisms (MDRO) detected.'
      }
• **Notification Checklist**:
  [ ${data.isCriticalAlert ? '✓' : ' '} ] Urgent Ward Call to Primary Care Team
  [ ${allResistanceAlerts.length > 0 ? '✓' : ' '} ] Notify Hospital Infection Control Committee
  [ ${data.approved ? '✓' : ' '} ] Diagnostic Report Frozen in Legal Audit Trail
      `
    }
  ];

  const currentOption = promptOptions.find((p) => p.id === selectedPrompt) || promptOptions[0];
  const responseText = currentOption.generate();

  return (
    <div className="micro-modal-overlay" style={{ zIndex: 1200 }} onClick={onClose}>
      <div className="wb-ai-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="wb-ai-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.4rem' }}>✨</span>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#fff' }}>
                Microbiology AI Clinical Copilot
              </h3>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
                Context-aware laboratory assistant &bull; Powered by master clinical rules
              </p>
            </div>
          </div>
          <button className="wb-drawer-close-btn" style={{ color: '#fff' }} onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Prompt Selector Pills */}
        <div className="wb-ai-prompt-pills-wrap">
          {promptOptions.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`wb-ai-prompt-pill ${selectedPrompt === opt.id ? 'active' : ''}`}
              onClick={() => setSelectedPrompt(opt.id)}
            >
              {opt.title}
            </button>
          ))}
        </div>

        {/* Response Box */}
        <div className="wb-ai-response-box">
          <div className="wb-ai-response-text">{responseText}</div>
        </div>

        {/* Clinical Disclaimer */}
        <div className="wb-ai-disclaimer">
          ⚠️ <strong>Clinical Safety Notice:</strong> AI Copilot provides contextual synthesis based strictly on active configured laboratory masters. The authorized microbiologist and treating clinician maintain final legal authority.
        </div>

        {/* Footer */}
        <div className="wb-ai-modal-footer">
          <button type="button" className="micro-btn-secondary" onClick={onClose}>
            Close Copilot
          </button>
        </div>
      </div>
    </div>
  );
}
